import { envVars } from "envvars";
import { useEffect, useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { ImagePreviewActionButtons } from "components/new/ImagePreview/ImagePreviewActionButtons";
import { Loader } from "components/new/Loader";
import { fileConfig, fileModyfierConfig } from "lib/tools/httpConfigs";
import { toaster } from "lib/tools/toaster";

type Props = {
  file: string;
  addFileText?: string;
  isModal?: boolean;
  handleAbort?: () => void;
  handleClose?: () => void;
};

export const ImagePreview = ({
  file,
  addFileText = "Dodaj do wiadomości",
  isModal,
  handleAbort,
  handleClose,
}: Props) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [isFileLoadedSuccessfully, setIsFileLoadedSuccessfully] =
    useState(true);
  const [imgTimestamp, setImgTimestamp] = useState<number>(
    new Date().getTime(),
  );

  useEffect(() => {
    setImageLoading(true);
    setIsFileLoadedSuccessfully(true);
  }, [file]);

  const src = useMemo<string>(() => {
    return (
      fileConfig[envVars.REACT_APP_CONFIG] +
      file +
      "&" +
      imgTimestamp.toString()
    );
  }, [imgTimestamp, file]);

  const modifyImage = useDebouncedCallback((queryParams: string) => {
    const defaultWarningMsg = "Coś poszło nie tak!";
    setImageLoading(true);

    fetch(fileModyfierConfig[envVars.REACT_APP_CONFIG] + "?" + queryParams)
      .then((response) => response.json())
      .then((response) => {
        if (!response.success) {
          toaster.warning(defaultWarningMsg);
          return;
        }
        setImgTimestamp(new Date().getTime());
        setImageLoading(false);
      })
      .catch(() => {
        toaster.warning(defaultWarningMsg);
        setImageLoading(false);
      });
  }, 700);

  if (!isFileLoadedSuccessfully) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <svg
          width="136"
          height="148"
          viewBox="0 0 136 148"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.0694 0.184479C5.77031 1.26071 1.66274 5.2337 0.326122 10.5752L0 11.8788V74.0146V136.15L0.326122 137.454C1.58117 142.47 5.34081 146.338 10.2504 147.666L11.4862 148H67.9723H124.458L125.694 147.666C130.145 146.463 133.728 143.116 135.218 138.771C136.073 136.277 136.024 139.93 135.98 82.3093C135.942 32.4417 135.925 29.4514 135.684 28.5421C135.048 26.1527 134.068 24.2125 132.675 22.5861C131.222 20.8911 113.135 3.04762 112.231 2.41782C111.06 1.60272 109.321 0.781694 107.883 0.365776L106.72 0.0292503L59.394 0.00540316C19.85 -0.0144448 11.9037 0.0150309 11.0694 0.184479ZM12.0966 9.50056C10.8589 9.95025 9.83658 10.9835 9.35692 12.2696C9.09579 12.9698 9.08721 14.9373 9.08721 74.0106C9.08721 134.748 9.08852 135.032 9.37916 135.823C9.71881 136.748 10.6727 137.831 11.585 138.327L12.2132 138.669H67.9723H123.731L124.36 138.327C125.272 137.831 126.226 136.748 126.565 135.823C126.856 135.033 126.857 134.736 126.857 82.9148C126.857 25.8407 126.925 30.2345 126.022 28.9977C125.802 28.696 122.677 25.5244 119.079 21.9495C115.48 18.3747 111.29 14.2063 109.767 12.6866C107.346 10.2703 106.893 9.8833 106.163 9.60499L105.328 9.28668L58.9886 9.2932C20.3635 9.29853 12.5574 9.33319 12.0966 9.50056ZM43.7838 27.8149C39.68 28.1733 35.8647 29.9293 32.8769 32.8345C30.2636 35.3757 28.5817 38.3652 27.667 42.095C27.3854 43.2432 27.34 43.8184 27.34 46.2423C27.34 48.6663 27.3854 49.2414 27.667 50.3897C28.8299 55.1311 31.4903 59.0586 35.2584 61.5965C38.4579 63.7515 41.5259 64.6953 45.369 64.707C49.0786 64.7182 52.4066 63.7081 55.4683 61.6412C61.0032 57.9051 64.1256 51.227 63.4709 44.5258C63.2359 42.1212 62.8543 40.7085 61.7436 38.1334C61.3256 37.1643 60.0156 35.1737 59.0546 34.0476C55.2962 29.6424 49.5784 27.3087 43.7838 27.8149ZM43.8022 37.1379C41.4275 37.5452 39.1426 39.1226 37.7109 41.3429C35.9196 44.1208 35.9196 48.3638 37.7109 51.1418C38.8794 52.954 40.8056 54.4794 42.6638 55.0643C48.4861 56.897 54.4506 52.4328 54.4506 46.2423C54.4506 40.5325 49.3645 36.1837 43.8022 37.1379ZM77.8282 68.8271C75.6383 69.3942 73.4905 70.7788 72.1555 72.4841C71.7197 73.0407 68.4021 77.8121 64.7829 83.0869C61.1639 88.3617 58.144 92.6776 58.0722 92.6776C58.0004 92.6776 56.3872 91.4122 54.4876 89.8656C49.6662 85.9398 48.4521 85.3587 45.0726 85.3586C43.1001 85.3585 42.0681 85.5916 40.4538 86.4015C39.2282 87.0166 38.1498 87.824 37.2706 88.7852C36.6764 89.4348 23.6747 109.207 23.1093 110.32C22.7266 111.074 22.5867 112.221 22.7682 113.117C22.9821 114.173 23.3202 114.833 24.0123 115.545C25.6427 117.223 28.1975 117.344 30.139 115.835C30.4536 115.591 33.011 111.807 37.1686 105.434C40.7627 99.9254 43.8982 95.2346 44.1359 95.0105C44.6945 94.4842 45.3556 94.475 46.0912 94.9837C46.3939 95.193 48.8075 97.1409 51.4548 99.3126C54.102 101.484 56.6258 103.459 57.063 103.701C57.7246 104.066 58.0299 104.141 58.8805 104.146C60.1594 104.154 60.9175 103.926 61.7426 103.284C62.2301 102.905 64.4463 99.7849 70.7915 90.5448C75.4135 83.8142 79.3342 78.208 79.5045 78.0866C79.9682 77.7558 80.9375 77.8035 81.3305 78.1765C81.5105 78.3474 87.0262 86.6957 93.5878 96.7284C100.149 106.761 105.755 115.225 106.046 115.538C107.343 116.931 109.402 117.311 111.148 116.479C113.311 115.448 114.39 112.625 113.427 110.518C113.239 110.107 107.589 101.373 100.872 91.1104C89.9609 74.4408 88.5498 72.3486 87.6404 71.4923C85.5428 69.5176 83.545 68.6835 80.6944 68.5924C79.3052 68.548 78.7211 68.5958 77.8282 68.8271Z"
            fill="#E2E2E9"
          />
        </svg>
      </div>
    );
  }

  const isFileNotUploaded = file.startsWith("blob");

  const rotateImg = (file: string) => {
    modifyImage(`rotate_right&image_id=${file}&rotations_num=1`);
  };

  return (
    <>
      {imageLoading && (
        <div className="absolute flex h-full w-full items-center justify-center">
          <Loader color="darkBlue" size="md" />
        </div>
      )}

      <div className={`flex h-[90%] w-full items-center justify-center`}>
        <img
          src={isFileNotUploaded ? file : src}
          className={`h-full w-full object-contain object-center ${
            imageLoading ? "hidden" : ""
          }`}
          onLoad={() => setImageLoading(false)}
          onError={() => setIsFileLoadedSuccessfully(false)}
        />
      </div>

      <ImagePreviewActionButtons
        isModal={Boolean(isModal)}
        file={file}
        addFileText={addFileText}
        handleRotateImage={rotateImg}
        handleAbort={handleAbort}
        handleClose={handleClose}
      />
    </>
  );
};
